// ── Filter Operators ──────────────────────────────────────────

export type EntityOperator =
  | 'is'
  | 'is_not'
  | 'is_any_of'
  | 'is_none_of'
  | 'is_empty'
  | 'is_not_empty';

export type TextOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'starts_with'
  | 'is_empty'
  | 'is_not_empty';

export type NumericOperator =
  | 'equals'
  | 'between'
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'is_empty'
  | 'is_not_empty';

export type DateOperator =
  | 'is'
  | 'is_within'
  | 'is_before'
  | 'is_after'
  | 'is_today'
  | 'is_past_7_days'
  | 'is_past_30_days'
  | 'is_empty'
  | 'is_not_empty';

export type FilterOperator =
  | EntityOperator
  | TextOperator
  | NumericOperator
  | DateOperator;

// ── Filter Field Definitions ──────────────────────────────────

export type FilterFieldType =
  | 'entity' // Multi-select / Select from dynamic options (workers, teams, etc.)
  | 'text' // Free text input
  | 'numeric' // Single / dual numeric input (age, etc.)
  | 'date' // Date picker / range
  | 'select'; // Multi-select / Select from static options (interest level, status)

export interface FilterFieldOption {
  value: string;
  label: string;
}

export interface FilterFieldDefinition {
  id: string; // Matches DB field name: 'worker', 'team', etc.
  label: string; // German display label
  category: string; // Grouping: 'Mitarbeiter', 'Vertrag', 'Kunde', 'Ort'
  type: FilterFieldType;
  operators: FilterOperator[]; // Allowed operators for this field
  options?: FilterFieldOption[]; // Static options (set at load time)
  loadOptions?: () => Promise<FilterFieldOption[]>; // Dynamic async loader
}

// ── Filter AST (Condition Tree) ───────────────────────────────

export type FilterValue =
  | string // Single text/select value
  | string[] // Multi-select values
  | number // Single numeric
  | { min: number; max: number } // Numeric range (age between)
  | { start: string; end: string } // Date range (YYYY-MM-DD)
  | null; // Empty state

export interface FilterCondition {
  id: string; // Unique ID (crypto.randomUUID())
  type: 'condition';
  field: string; // FilterFieldDefinition.id
  operator: FilterOperator;
  value: FilterValue;
}

export interface FilterGroup {
  id: string;
  type: 'group';
  conjunction: 'and' | 'or';
  children: Array<FilterCondition | FilterGroup>;
}

// ── Saved Presets ─────────────────────────────────────────────

export interface HeatmapSavedFilter {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  filter_config: FilterGroup;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

// ── System Presets ────────────────────────────────────────────

export interface SystemPreset {
  id: string; // e.g. 'system_nicht_geoeffnet'
  name: string;
  description: string;
  filter_config: FilterGroup;
  isSystem: true;
}

// ── AST Helpers ───────────────────────────────────────────────

export function isFilterGroup(
  node: FilterCondition | FilterGroup
): node is FilterGroup {
  return node && node.type === 'group';
}

export function isFilterCondition(
  node: FilterCondition | FilterGroup
): node is FilterCondition {
  return node && node.type === 'condition';
}

export function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
}

export function createEmptyFilterCondition(
  field = 'worker',
  operator: FilterOperator = 'is',
  value: FilterValue = null
): FilterCondition {
  return {
    id: generateId(),
    type: 'condition',
    field,
    operator,
    value,
  };
}

export function createEmptyFilterGroup(
  conjunction: 'and' | 'or' = 'and',
  children: Array<FilterCondition | FilterGroup> = []
): FilterGroup {
  return {
    id: generateId(),
    type: 'group',
    conjunction,
    children,
  };
}

/**
 * Recursively counts all non-empty filter conditions in the filter tree.
 */
export function countActiveConditions(group: FilterGroup | null): number {
  if (!group || !Array.isArray(group.children)) {
    return 0;
  }

  let count = 0;
  for (const child of group.children) {
    if (isFilterGroup(child)) {
      count += countActiveConditions(child);
    } else if (isFilterCondition(child)) {
      // Condition is active if it has a non-null/non-empty value, or operator requires no value
      const noValueOperators: FilterOperator[] = [
        'is_empty',
        'is_not_empty',
        'is_today',
        'is_past_7_days',
        'is_past_30_days',
      ];
      if (noValueOperators.includes(child.operator)) {
        count++;
      } else if (child.value !== null && child.value !== undefined) {
        if (Array.isArray(child.value)) {
          if (child.value.length > 0) count++;
        } else if (typeof child.value === 'string') {
          if (child.value.trim() !== '') count++;
        } else if (typeof child.value === 'number') {
          count++;
        } else if (typeof child.value === 'object') {
          // range object
          count++;
        }
      }
    }
  }

  return count;
}
